// uploadUtils.js — Drop this file into your src/ folder
// Handles: image compression, resumable uploads, queued processing, duplicate detection
// Video compression intentionally excluded to avoid COEP header conflicts with Stripe

import imageCompression from 'browser-image-compression';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage, db } from './firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

// ============================================================
// DUPLICATE DETECTION
// Creates a fingerprint from filename + size + type to catch re-uploads
// ============================================================
export async function checkForDuplicates(files, userId) {
  try {
    const q = query(collection(db, 'memories'), where('userId', '==', userId));
    const snapshot = await getDocs(q);
    
    // Build a set of existing fingerprints
    const existingFingerprints = new Set();
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      // Create fingerprint from name + size (size stored as string in MB)
      const fingerprint = `${data.name}_${data.size}_${data.type}`;
      existingFingerprints.add(fingerprint);
    });

    const newFiles = [];
    const duplicates = [];

    files.forEach(file => {
      const sizeMB = (file.size / 1024 / 1024).toFixed(2);
      const type = file.type.startsWith('image') ? 'image' : 'video';
      const fingerprint = `${file.name}_${sizeMB}_${type}`;

      if (existingFingerprints.has(fingerprint)) {
        duplicates.push(file.name);
      } else {
        newFiles.push(file);
        // Also add to set so we catch duplicates within the same batch
        existingFingerprints.add(fingerprint);
      }
    });

    return { newFiles, duplicates };
  } catch (error) {
    console.warn('⚠️ Duplicate check failed, proceeding with all files:', error);
    return { newFiles: files, duplicates: [] };
  }
}

// ============================================================
// IMAGE COMPRESSION
// ============================================================
export async function compressImage(file) {
  // Skip if already small (under 500KB)
  if (file.size < 500 * 1024) {
    console.log(`⚡ ${file.name} is already small (${(file.size / 1024).toFixed(0)}KB), skipping compression`);
    return file;
  }

  const options = {
    maxSizeMB: 2,           // Target max 2MB per image
    maxWidthOrHeight: 2048, // Keep resolution reasonable
    useWebWorker: true,     // Don't block the UI thread
    fileType: 'image/jpeg', // Convert HEIC/PNG to JPEG for smaller size
    preserveExif: true,     // KEEP EXIF — we need GPS + date for festival detection!
  };

  try {
    const originalSize = file.size;
    const compressed = await imageCompression(file, options);
    const savedPercent = ((1 - compressed.size / originalSize) * 100).toFixed(0);
    console.log(`📦 Compressed ${file.name}: ${(originalSize / 1024 / 1024).toFixed(1)}MB → ${(compressed.size / 1024 / 1024).toFixed(1)}MB (${savedPercent}% smaller)`);
    
    // Keep original filename
    return new File([compressed], file.name, { type: compressed.type });
  } catch (error) {
    console.warn(`⚠️ Compression failed for ${file.name}, using original:`, error);
    return file; // Fall back to original
  }
}

// ============================================================
// RESUMABLE UPLOAD WITH PROGRESS
// ============================================================
export function uploadFileResumable(file, userId, onProgress) {
  return new Promise((resolve, reject) => {
    const timestamp = Date.now();
    const fileRef = ref(storage, `memories/${userId}/${timestamp}_${file.name}`);
    
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on('state_changed',
      // Progress callback
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) onProgress(progress, snapshot.state);
      },
      // Error callback
      (error) => {
        console.error(`❌ Upload failed for ${file.name}:`, error);
        if (error.code === 'storage/canceled') {
          reject(new Error('Upload cancelled'));
        } else {
          reject(error);
        }
      },
      // Success callback
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        } catch (error) {
          reject(error);
        }
      }
    );
  });
}

// ============================================================
// QUEUED UPLOAD PROCESSOR
// Processes files 2 at a time to prevent overwhelming the browser
// ============================================================
export async function processUploadQueue(files, userId, callbacks) {
  const {
    onFileStart,      // (index, file, stage) => {}
    onFileProgress,   // (index, file, progress, stage) => {}
    onFileComplete,   // (index, file, result) => {}
    onFileError,      // (index, file, error) => {}
    onQueueComplete,  // (results) => {}
  } = callbacks;

  const results = [];
  const CONCURRENT_UPLOADS = 2; // Process 2 files at a time

  for (let i = 0; i < files.length; i += CONCURRENT_UPLOADS) {
    const batch = files.slice(i, i + CONCURRENT_UPLOADS);
    
    const batchPromises = batch.map(async (file, batchIndex) => {
      const index = i + batchIndex;
      
      try {
        // STAGE 1: Compress (images only — videos upload as-is for now)
        if (onFileStart) onFileStart(index, file, 'compressing');
        
        let processedFile;
        if (file.type.startsWith('image')) {
          processedFile = await compressImage(file);
        } else {
          // Videos upload as-is (compression requires COEP headers that break Stripe)
          processedFile = file;
        }

        // STAGE 2: Upload with resumable upload
        if (onFileStart) onFileStart(index, file, 'uploading');
        
        const downloadURL = await uploadFileResumable(
          processedFile, 
          userId,
          (progress, state) => {
            if (onFileProgress) onFileProgress(index, file, progress, 'uploading');
          }
        );

        const result = { 
          index, 
          file: processedFile, 
          originalFile: file, 
          url: downloadURL,
          compressedSize: processedFile.size,
          originalSize: file.size,
        };
        
        results.push(result);
        if (onFileComplete) onFileComplete(index, file, result);
        
        return result;
      } catch (error) {
        console.error(`❌ Failed to process ${file.name}:`, error);
        if (onFileError) onFileError(index, file, error);
        results.push({ index, file, error });
        return null;
      }
    });

    await Promise.all(batchPromises);
  }

  if (onQueueComplete) onQueueComplete(results);
  return results;
}